// src/components/SendWorkModal.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiSend, FiAlertTriangle, FiLoader } from 'react-icons/fi';
import {
  prepareWorkSend,
  sendWorkNotice,
} from '../../services/workService';

/* -------------------------------- utils -------------------------------- */

const stripUndefined = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function parseEmails(input) {
  if (!input) return [];
  return String(input)
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function firstNameOf(raw) {
  if (!raw) return '';
  const first = String(raw).trim().split(/\s+/)[0];
  return first || '';
}

const fmtWhen = (sch) => {
  if (!sch?.start) return '';
  try {
    if (sch.allDay) {
      return new Date(sch.start).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
    const s = new Date(sch.start);
    const e = sch.end ? new Date(sch.end) : null;
    const sStr = s.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const eStr = e ? e.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
    return eStr ? `${sStr}–${eStr}` : sStr;
  } catch {
    return '';
  }
};

/** Accept both 'complete' and 'completed', return backend-friendly */
const normalizeTypeForAPI = (t) => {
  const s = String(t || 'confirm').toLowerCase();
  return s === 'complete' ? 'completed' : s;
};

/** Subject line for UI; accept 'complete' and 'completed' */
function subjectFor(type, workTitle, schedule) {
  const t = normalizeTypeForAPI(type);
  const when = fmtWhen(schedule);
  const title = workTitle || (t === 'reminder' ? 'Upcoming job' : 'Work order');
  const suffix = when ? ` — ${when}` : '';
  switch (t) {
    case 'confirm':   return `Appointment confirmed — ${title}${suffix}`;
    case 'reminder':  return `Reminder — ${title}${suffix}`;
    case 'completed': return `Completed — ${title}`;
    default:          return `Work notice — ${title}${suffix}`;
  }
}

/** Message line for UI; accept 'complete' and 'completed' */
function shortDefaultFor(type, clientName, schedule) {
  const t = normalizeTypeForAPI(type);
  const first = firstNameOf(clientName);
  const hi = first ? `Hi ${first}, ` : '';
  const when = fmtWhen(schedule);
  switch (t) {
    case 'confirm':   return `${hi}your appointment is confirmed${when ? ` for ${when}` : ''}.`;
    case 'reminder':  return `${hi}this is a reminder for your appointment${when ? ` on ${when}` : ''}.`;
    case 'completed': return `${hi}we’ve marked your job as complete.`;
    default:          return `${hi}please review this message.`;
  }
}

function useEscapeToClose(enabled, onClose) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onClose]);
}

function useFocusOnOpen(isOpen, ref) {
  useEffect(() => {
    if (isOpen && ref?.current) {
      const t = setTimeout(() => ref.current?.focus?.(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen, ref]);
}

// only keep valid unique emails
function collectValidEmails(list) {
  const flat = []
    .concat(...(Array.isArray(list) ? list : [list]))
    .flatMap((v) => (Array.isArray(v) ? v : [v]));
  const cleaned = flat
    .filter((v) => typeof v === 'string')
    .map((s) => s.trim())
    .filter((s) => emailRegex.test(s));
  return Array.from(new Set(cleaned));
}

/* ---------------------------- small UI helpers --------------------------- */

const Switch = ({ checked, onChange, disabled, id }) => (
  <label
    htmlFor={id}
    className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
  >
    <input type="checkbox" id={id} className="sr-only peer" checked={checked} onChange={onChange} disabled={disabled} />
    <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-400 dark:peer-focus:ring-indigo-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 dark:after:border-neutral-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-indigo-600" />
  </label>
);

const Field = ({ label, children, hint, error }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</label>
    <div className="mt-2">{children}</div>
    {hint && !error && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    {error && (
      <p className="mt-1 text-xs text-rose-600 inline-flex items-center gap-1">
        <FiAlertTriangle /> {error}
      </p>
    )}
  </div>
);

/* -------------------------------- component ----------------------------- */

export default function SendWorkModal({
  isOpen,
  onClose,
  work,
  onSent,
  lastSentAt,             // optional initial timestamp from parent
  type: typeProp,         // optional: 'confirm' | 'reminder' | 'completed' | 'complete'
  initialType,            // optional: legacy prop
  prefillTo,              // string | string[]
  prefillCc,              // string | string[]
  clientNameOverride,     // string
  scheduleOverride,       // object { start, end, allDay, timezone }
  onSend,                 // optional: (type, payload) => Promise<{ status, queued, ... }>
}) {
  const toInputRef = useRef(null);

  // id
  const workId = useMemo(
    () => (work?.id || work?._id || work?.workId ? String(work.id || work._id || work.workId) : ''),
    [work]
  );

  // basics
  const schedule   = scheduleOverride || work?.schedule || {};
  const workTitle  = work?.title || work?.name || '';
  const workNumber = work?.workNumber || work?.number || '';

  // widen email/name sources + allow explicit prefills
  const cs   = work?.clientSnapshot || {};
  const bill = work?.billTo || {};
  const toCandidates = collectValidEmails([
    prefillTo,
    work?.client?.email,
    work?.clientEmail,
    cs?.email,
    cs?.contactEmail,
    cs?.contact?.email,
    cs?.primaryContact?.email,
    bill?.email,
    bill?.contact?.email,
    Array.isArray(cs?.emails) ? cs.emails : [],
    Array.isArray(work?.emails) ? work.emails : [],
  ]);
  const ccCandidates = collectValidEmails(prefillCc || []);
  const clientEmailPrefill = toCandidates.join(', ');
  const ccPrefill          = ccCandidates.join(', ');

  const clientName =
    clientNameOverride ||
    work?.client?.name ||
    work?.clientName ||
    cs?.name ||
    cs?.contactName ||
    cs?.contact?.name ||
    cs?.primaryContact?.name ||
    bill?.contactName ||
    'Client';

  // figure out the initial type (prefer prop `type`, then `initialType`, else infer from status)
  const preferredInitialType = useMemo(() => {
    const raw = (typeProp || initialType || '').toString().toLowerCase();
    if (['confirm', 'reminder', 'complete', 'completed'].includes(raw)) return raw;
    const status = String(work?.status || work?.lifecycleStatus || '').toLowerCase();
    if (status.includes('scheduled') || status === 'todo') return 'confirm';
    if (status.includes('in_progress')) return 'reminder';
    if (status.includes('done') || status.includes('complete')) return 'completed';
    return 'confirm';
  }, [typeProp, initialType, work]);

  // state
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [type, setType] = useState(preferredInitialType); // UI can hold 'complete' or 'completed'
  const [toRaw, setToRaw] = useState(clientEmailPrefill);
  const [ccRaw, setCcRaw] = useState(ccPrefill);
  const [message, setMessage] = useState(shortDefaultFor(preferredInitialType, clientName, schedule));
  const [force, setForce] = useState(false);
  const [subjectPreview, setSubjectPreview] = useState(subjectFor(preferredInitialType, workTitle, schedule));
  const [apiWarning, setApiWarning] = useState(null);

  // last sent timestamp (idempotency chip)
  const [lastSent, setLastSent] = useState(lastSentAt || null);
  useEffect(() => { setLastSent(lastSentAt || null); }, [lastSentAt]);

  // derived email lists + validation
  const toList = useMemo(() => parseEmails(toRaw), [toRaw]);
  const ccList = useMemo(() => parseEmails(ccRaw), [ccRaw]);
  const invalidTo = useMemo(() => toList.filter((e) => !emailRegex.test(e)), [toList]);
  const invalidCc = useMemo(() => ccList.filter((e) => !emailRegex.test(e)), [ccList]);

  const anyBusy = preparing || submitting;

  useEscapeToClose(isOpen && !anyBusy, onClose);
  useFocusOnOpen(isOpen, toInputRef);

  // reset & base prefill on open or when work changes
  useEffect(() => {
    if (!isOpen || !workId) return;

    const t = preferredInitialType;
    setType(t);
    setToRaw(clientEmailPrefill || '');
    setCcRaw(ccPrefill || '');
    setMessage(shortDefaultFor(t, clientName, schedule));
    setForce(false);
    setSubjectPreview(subjectFor(t, workTitle, schedule));
    setApiWarning(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workId]);

  // server prefill (prepareWorkSend)
  useEffect(() => {
    if (!isOpen || !workId) return;

    let cancelled = false;
    (async () => {
      try {
        setPreparing(true);
        const pre = await prepareWorkSend(workId, { type: normalizeTypeForAPI(type) });
        if (cancelled || !pre) return;

        const preTo = Array.isArray(pre.to) ? pre.to.join(', ') : (pre.to || '');
        const preCc = Array.isArray(pre.cc) ? pre.cc.join(', ') : (pre.cc || '');

        if (preTo) setToRaw(preTo);
        if (preCc) setCcRaw(preCc);
        if (pre.subject) setSubjectPreview(pre.subject);
        if (pre.message) setMessage(pre.message);
        if (pre.lastSentAt) setLastSent(pre.lastSentAt);
      } catch (e) {
        // non-fatal: fall back to local defaults
        toast.warn(e?.message || 'Could not prefill email; using defaults.');
      } finally {
        setPreparing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, workId, type]);

  // sync subject/message when type changes (unless user customized)
  useEffect(() => {
    setSubjectPreview((prev) => {
      const auto = subjectFor(type, workTitle, schedule);
      return prev &&
        prev !== subjectFor('confirm', workTitle, schedule) &&
        prev !== subjectFor('reminder', workTitle, schedule) &&
        prev !== subjectFor('completed', workTitle, schedule)
        ? prev
        : auto;
    });

    const defaults = new Set([
      shortDefaultFor('confirm',  clientName, schedule),
      shortDefaultFor('reminder', clientName, schedule),
      shortDefaultFor('completed', clientName, schedule),
      '',
    ]);
    if (defaults.has(message)) {
      setMessage(shortDefaultFor(type, clientName, schedule));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, workTitle, clientName, schedule?.start, schedule?.end, schedule?.allDay]);

  const relativeLastSent = useMemo(() => {
    if (!lastSent) return null;
    try {
      const then = new Date(lastSent).getTime();
      const now = Date.now();
      const diffSec = Math.max(0, Math.round((now - then) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffH = Math.round(diffMin / 60);
      if (diffH < 24) return `${diffH}h ago`;
      const diffD = Math.round(diffH / 24);
      return `${diffD}d ago`;
    } catch {
      return null;
    }
  }, [lastSent]);

  const inIdemWindow = useMemo(() => {
    if (!lastSent) return false;
    const then = new Date(lastSent).getTime();
    return Date.now() - then < 15 * 60 * 1000;
  }, [lastSent]);

  const canSubmit =
    toList.length > 0 &&
    invalidTo.length === 0 &&
    invalidCc.length === 0 &&
    !submitting &&
    !preparing;

  const buildPayload = useCallback(() => {
    return stripUndefined({
      type: normalizeTypeForAPI(type),
      to: toList.length ? toList : undefined,
      cc: ccList.length ? ccList : undefined,
      message: message?.trim() ? message.trim() : undefined,
      force: !!force || undefined,
    });
  }, [type, toList, ccList, message, force]);

  const handleSubmit = useCallback(async () => {
    if (!workId) {
      toast.error('Work item not found.');
      return;
    }
    if (!canSubmit) return;

    setSubmitting(true);
    setApiWarning(null);

    try {
      const payload = buildPayload();
      const doSend = typeof onSend === 'function'
        ? () => onSend(normalizeTypeForAPI(type), payload)
        : () => sendWorkNotice(workId, payload);

      const result = await doSend();

      if (result?.status === 'duplicate') {
        toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
        onSent?.(result);
        return;
      }

      if (result?.rateLimited) {
        toast.warn('Rate limit reached. Try again shortly.');
        setApiWarning({ title: 'Rate limit reached', detail: 'Please wait a bit before trying again.' });
        onSent?.(result);
        return;
      }

      if (result?.queued) toast.success('Queued for delivery.');
      else toast.success('Email sent.');

      // Update local lastSent so idempotency chip reflects immediately
      setLastSent(new Date().toISOString());

      onSent?.(result);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status || e?.status;
      const data = e?.response?.data || e?.data;

      if (status === 429) {
        setApiWarning({
          title: 'Rate limit reached',
          detail: data?.message || 'You have reached the sending limit. Please try again later.',
        });
        toast.warn('Rate limit reached.');
      } else if (status === 400 && data?.suppression) {
        setApiWarning({
          title: 'Address suppressed',
          detail: data?.suppression?.reason || 'The recipient is suppressed due to previous bounces or complaints.',
        });
        toast.warn('Recipient is suppressed.');
      } else if (status === 409) {
        toast.info('Already sent recently (idempotent). Enable “Force resend” to override.');
      } else {
        toast.error(e?.message || 'Failed to send.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [workId, canSubmit, buildPayload, onSend, onSent, onClose, type]);

  const onKeyDown = useCallback(
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [canSubmit, handleSubmit]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onKeyDown={onKeyDown}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300 ease-in-out"
        onClick={preparing || submitting ? undefined : onClose}
        aria-hidden="true"
      />
      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-work-title"
        className="absolute inset-0 flex items-end sm:items-center justify-center p-4"
      >
        <div className="w-full sm:max-w-3xl rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden dark:border dark:border-neutral-700/80">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
            <div className="min-w-0">
              <h3 id="send-work-title" className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                Send Work Notice
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 truncate">
                {workNumber ? `${workNumber} — ` : null}{workTitle || 'Work'} • {clientName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={preparing || submitting}
              className="p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-500 dark:text-neutral-400 disabled:opacity-50 transition-colors"
              aria-label="Close"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-6 space-y-5 relative max-h-[70vh] overflow-y-auto">
            {preparing && (
              <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm z-10 pointer-events-none flex items-center justify-center">
                <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300">
                  <FiLoader className="animate-spin" />
                  Loading…
                </div>
              </div>
            )}

            {apiWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 dark:border-amber-700/50 px-4 py-3 text-sm">
                <div className="flex items-start gap-3">
                  <FiAlertTriangle className="mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <div className="font-medium dark:text-amber-100">{apiWarning.title}</div>
                    <div className="mt-0.5">{apiWarning.detail}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Subject preview */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">Subject</label>
              <p className="mt-2 text-sm text-neutral-800 dark:text-neutral-200 truncate">
                {subjectPreview}
              </p>
            </div>

            {/* Type selector */}
            <Field label="Type">
              <div className="grid grid-cols-3 gap-2">
                {['confirm', 'reminder', 'completed'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setType(opt)}
                    className={[
                      'h-10 rounded-lg border text-sm transition-colors',
                      type === opt
                        ? 'border-indigo-600 text-indigo-700 bg-indigo-50 dark:bg-indigo-900/40 dark:text-indigo-200'
                        : 'border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700',
                    ].join(' ')}
                  >
                    {opt === 'confirm' ? 'Confirmation' : opt === 'reminder' ? 'Reminder' : 'Completion'}
                  </button>
                ))}
              </div>
            </Field>

            {/* fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="To"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidTo.length
                    ? `Invalid email${invalidTo.length > 1 ? 's' : ''}: ${invalidTo.join(', ')}`
                    : undefined
                }
              >
                <input
                  ref={toInputRef}
                  type="text"
                  value={toRaw}
                  onChange={(e) => setToRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="client@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {toList.slice(0, 6).map((e) => (
                    <span
                      key={e}
                      className="inline-flex items-center rounded-md bg-indigo-100/80 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-1 text-xs font-medium"
                    >
                      {e}
                    </span>
                  ))}
                  {toList.length > 6 && (
                    <span className="text-xs text-neutral-500 dark:text-neutral-400 py-1">
                      +{toList.length - 6} more
                    </span>
                  )}
                </div>
              </Field>

              <Field
                label="Cc (optional)"
                hint="Separate multiple emails with commas or spaces."
                error={
                  invalidCc.length
                    ? `Invalid email${invalidCc.length > 1 ? 's' : ''}: ${invalidCc.join(', ')}`
                    : undefined
                }
              >
                <input
                  type="text"
                  value={ccRaw}
                  onChange={(e) => setCcRaw(e.target.value)}
                  className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 transition"
                  placeholder="team@example.com"
                  disabled={preparing || submitting}
                  autoComplete="email"
                />
              </Field>
            </div>

            <Field label="Message (optional)" hint="Short note included at the top of the email.">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-lg border-0 bg-neutral-100 dark:bg-neutral-800 px-3.5 py-2.5 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 min-h-[96px] transition"
                placeholder={shortDefaultFor(type, clientName, schedule)}
                disabled={preparing || submitting}
              />
            </Field>

            {/* toggles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 pt-1">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <label htmlFor="force-resend-toggle" className="font-medium text-neutral-800 dark:text-neutral-100 cursor-pointer">
                    Force Resend
                  </label>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    Override 15min duplicate check
                    {relativeLastSent ? ` (${relativeLastSent})` : ''}.
                    {inIdemWindow && <span className="ml-1.5 text-amber-600">(within window)</span>}
                  </div>
                </div>
                <Switch
                  id="force-resend-toggle"
                  checked={!!force}
                  onChange={(e) => setForce(e.target.checked)}
                  disabled={preparing || submitting}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={preparing || submitting}
                className="h-10 px-4 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="h-10 px-4 rounded-lg text-white shadow-sm flex items-center gap-2 disabled:opacity-50 bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
              >
                {submitting ? <FiLoader className="animate-spin" /> : <FiSend size={16} />}
                {normalizeTypeForAPI(type) === 'confirm'
                  ? 'Send Confirmation'
                  : normalizeTypeForAPI(type) === 'reminder'
                  ? 'Send Reminder'
                  : 'Send Completion'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
